import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	FormControl,
	FormControlLabel,
	FormLabel,
	InputAdornment,
	Radio,
	RadioGroup,
	TextField,
} from "@mui/material"
import { ChangeEvent, useEffect, useState } from "react"
import { Calendar } from "react-date-range"
import * as locales from "react-date-range/dist/locale"
import "react-date-range/dist/styles.css" // main css file
import "react-date-range/dist/theme/default.css" // theme css file

import { format, parse } from "date-fns"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { AppDispatch } from "../../../store"
import { PlickState } from "../../../store/actions/file-detail"
import { PlickResponse, updatePlick } from "../../../store/actions/plick-detail"
import { updatePatchForId } from "../../../store/plicks-list/plicks-slice"
import { validateEmail } from "../../../utils/check-email"
import { isValidPhoneNumber } from "../../../utils/sepa-phone-validator"
import AndroidSwitch from "../../common/android-switch"
import SepaPhoneNumberInput from "./sepaPhoneNumberInput"

type FormValues = {
	[key: string]: string | undefined
}

export type FormErrors = {
	[key: string]: string | undefined
}

const parseValidationErrors = (
	validationErrors: string | undefined
): FormErrors =>
	validationErrors
		? validationErrors.split(" - ").reduce((acc, error) => {
				const [field, errorType] = error.split(".")
				const camelCaseField = field
					.toLowerCase()
					.split("_")
					.map((word, index) =>
						index === 0
							? word
							: word.charAt(0).toUpperCase() + word.slice(1)
					)
					.join("")
				acc[camelCaseField] = errorType
				return acc
			}, {} as FormErrors)
		: {}

const PlickDrawerForm = ({
	currentPlick,
	isSavable,
	setIsSavable,
	setIsDrawerOpen,
}: {
	currentPlick: PlickResponse | undefined
	isSavable: boolean
	setIsSavable: (isSavable: boolean) => void
	setIsDrawerOpen: (isOpen: boolean) => void
}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()
	const [updateFormData, setUpdateFormData] = useState<PlickState | undefined>(
		undefined
	)
	const [errorsList, setErrorsList] = useState<FormErrors | undefined>(
		undefined
	)

	const [isSavingForm, setIsSavingForm] = useState<boolean>(false)

	const [senderDeviceType, setSenderDeviceType] = useState<string | undefined>(
		undefined
	)
	const [receiverDeviceType, setReceiverDeviceType] = useState<
		string | undefined
	>(undefined)

	const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)
	// Stato per i valori dei campi
	const [formValues, setFormValues] = useState<FormValues>({
		transactionId: "",
		senderCompany: "",
		senderDevice: "",
		senderLanguagePreferences: "",
		senderExtraInfo: "",
		receiverName: "",
		receiverDevice: "",
		receiverIban: "",
		receiverIbanFromBank: "false",
		activationDate: "",
		amount: "",
		causal: "",
		pin: "",
		pinType: "FISCAL_CODE",
		checkIbanCode: "",
	})

	useEffect(() => {
		setFormValues({
			transactionId: currentPlick?.transactionId,
			senderCompany: currentPlick?.senderCompany,
			senderDevice: currentPlick?.senderDevice,
			senderLanguagePreferences: currentPlick?.senderLanguagePreferences,
			senderExtraInfo: currentPlick?.senderExtraInfo,
			receiverName: currentPlick?.receiverName,
			receiverDevice: currentPlick?.receiverDevice,
			receiverIban: currentPlick?.receiverIban,
			receiverIbanFromBank: currentPlick?.receiverIbanFromBank,
			activationDate: currentPlick?.activationDate,
			amount: currentPlick?.amount,
			causal: currentPlick?.causal,
			pin: currentPlick?.pin,
			pinType: currentPlick?.pinType,
			checkIbanCode: currentPlick?.checkIbanCode,
		})
		setErrorsList(parseValidationErrors(currentPlick?.validationErrors))
		setUpdateFormData(undefined)
	}, [currentPlick])

	useEffect(() => {
		let noError = false
		if (
			!errorsList ||
			(Object.keys(errorsList).length === 0 &&
				errorsList.constructor === Object)
		) {
			noError = true
		}
		setIsSavable(noError)
	}, [errorsList])

	// Gestore per aggiornare i valori dei campi
	const handleChange =
		(field: string) => (event: ChangeEvent<HTMLInputElement> | string) => {
			let newValue = event?.target?.value ?? event
			let newErrors: FormErrors | undefined = { ...errorsList }
			// VALIDATION
			switch (field) {
				case "activationDate": {
					const { ["activationDate"]: _, ...remainingErrors } = newErrors
					newErrors = remainingErrors
					setErrorsList(newErrors)
					setOpenDatePicker(false)
					break
				}
				case "amount": {
					newValue = newValue.replace(",", ".")
					const regexpNumber = new RegExp("\\d+(\\.\\d+)?")
					if (!newValue) {
						newErrors.amount = "notPresent"
					}
					if (!regexpNumber.test(newValue)) {
						newErrors.amount = "formatWrong"
					} else {
						const { ["amount"]: _, ...remainingErrors } = newErrors
						newErrors = remainingErrors
					}
					setErrorsList(newErrors)
					break
				}
				case "senderDevice": {
					const isValidNumber = isValidPhoneNumber(newValue)
					if (!newValue) {
						newErrors.senderDevice = "notPresent"
					} else if (!isValidNumber && !validateEmail(newValue)) {
						newErrors.senderDevice = "formatWrong"
					} else {
						const { ["senderDevice"]: _, ...remainingErrors } = newErrors
						newErrors = remainingErrors
					}
					setErrorsList(newErrors)
					break
				}
				case "receiverDevice": {
					const isValidNumber = isValidPhoneNumber(newValue)
					if (!newValue) {
						newErrors.receiverDevice = "notPresent"
					} else if (!isValidNumber && !validateEmail(newValue)) {
						newErrors.receiverDevice = "formatWrong"
					} else {
						const { ["receiverDevice"]: _, ...remainingErrors } = newErrors
						newErrors = remainingErrors
					}
					setErrorsList(newErrors)
					break
				}
				default: {
					const { [field]: _, ...remainingErrors } = newErrors
					setErrorsList(remainingErrors)
					break
				}
			}

			setFormValues((prevValues) => ({
				...prevValues,
				[field]: newValue,
			}))

			setUpdateFormData((prevData) => ({
				...prevData,
				[field]: newValue,
			}))
		}

	const handleClickToggleDatePicker = () => {
		setOpenDatePicker(!openDatePicker)
	}

	const handleUpdate = async () => {
		setIsSavingForm(true)
		try {
			const updatedPlick = await dispatch(
				updatePlick({
					plickData: updateFormData,
					idBank: currentPlick?.idBank,
					idPlick: currentPlick?.id,
				})
			).unwrap()

			dispatch(
				updatePatchForId({
					id: currentPlick?.id,
					updatedPlick,
				})
			)

			if (updatedPlick.correctPlick) {
				setIsDrawerOpen(false)
			} else {
				setErrorsList(parseValidationErrors(updatedPlick.validationErrors))
				setUpdateFormData(undefined)
			}
		} catch {
			setErrorsList(parseValidationErrors(currentPlick?.validationErrors))
		} finally {
			setIsSavingForm(false)
		}
	}

	return (
		<Box p={2} sx={{ minWidth: "360px", width: "45vw" }}>
			{/* Content */}
			<Box sx={{ flex: 1, p: 2, overflowY: "auto" }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					{Object.keys(formValues).map((field) => {
						switch (field) {
							case "activationDate": {
								const localesSelected =
									window.localStorage.getItem("i18nextLng") === "en"
										? "enGB"
										: (window.localStorage.getItem("i18nextLng") ?? "it")
								const parsedStringDate = formValues?.[field]
									? typeof formValues?.[field] === "string"
										? parse(formValues?.[field], "yyyy-mm-dd", new Date())
										: formValues?.[field]
									: new Date()
								return (
									<>
										<TextField
											InputLabelProps={{ shrink: true }}
											id="activationDate"
											label={t("form.activationDate", { ns: "plick" })}
											placeholder="dd/mm/yyyy"
											value={
												formValues[field]
													? format(parsedStringDate, "dd/MM/yyyy")
													: ""
											}
											inputProps={{ readOnly: true }}
											error={Boolean(errorsList && errorsList?.[field])} // Mostra errore solo se presente
											helperText={
												errorsList && errorsList?.[field]
													? `Error: ${errorsList?.[field]}`
													: ""
											} // Messaggio di errore
											disabled={
												!errorsList ||
												(!errorsList?.[field] &&
													currentPlick[field] === formValues[field])
											}
											InputProps={{
												readOnly: true,
												endAdornment: (
													<InputAdornment position="start">
														<CalendarTodayIcon />
													</InputAdornment>
												),
											}}
											sx={{}}
											variant="outlined"
											onClick={handleClickToggleDatePicker}
										/>
										<Dialog
											sx={{ maxWidth: "60vw" }}
											open={openDatePicker}
											onClose={handleClickToggleDatePicker}
										>
											<DialogContent>
												<Calendar
													error={Boolean(errorsList && errorsList[field])} // Mostra errore solo se presente
													helperText={
														errorsList && errorsList[field]
															? `Error: ${errorsList[field]}`
															: ""
													}
													onChange={handleChange(field)}
													minDate={new Date()}
													locale={locales[localesSelected][localesSelected]}
													date={formValues.activationDate}
												/>
											</DialogContent>
										</Dialog>
									</>
								)
							}
							case "senderDevice":
							case "receiverDevice": {
								const channelType =
									!!errorsList?.[field] ||
									currentPlick?.[field] !== formValues?.[field] ? (
										<FormControl>
											<FormLabel id="channel-radio-buttons-group-label">
												{t("form.channel", { ns: "plick" })}
											</FormLabel>
											<RadioGroup
												row
												aria-labelledby="channel-radio-buttons-group-label"
												defaultValue={
													validateEmail(formValues?.[field])
														? "mail"
														: "phone"
												}
												name="channel-radio-buttons-group"
												onChange={(event: ChangeEvent<HTMLInputElement>) => {
													const changedValue = (
														event.target as HTMLInputElement
													).value
													handleChange(field)("")
													if (field === "receiverDevice")
														setReceiverDeviceType(changedValue)
													else {
														setSenderDeviceType(changedValue)
													}
												}}
											>
												<FormControlLabel
													value="mail"
													control={<Radio />}
													label={t("form.email", { ns: "plick" })}
												/>
												<FormControlLabel
													value="phone"
													control={<Radio />}
													label={t("form.phone", { ns: "plick" })}
												/>
											</RadioGroup>
										</FormControl>
									) : undefined

								return validateEmail(formValues?.[field]) ||
									(field === "receiverDevice" &&
										receiverDeviceType === "mail") ||
									(field === "senderDevice" && senderDeviceType === "mail") ? (
									<>
										{channelType}
										<TextField
											key={field}
											label={t(`form.${field.replace("_", " ")}`, {
												ns: "plick",
											})} // Etichetta leggibile
											value={formValues[field]}
											onChange={(event: ChangeEvent<HTMLInputElement>) =>
												handleChange(field)(event)
											}
											error={Boolean(errorsList && errorsList[field])} // Mostra errore solo se presente
											helperText={
												errorsList && errorsList[field]
													? `Error: ${errorsList[field]}`
													: ""
											} // Messaggio di errore
											InputProps={{
												readOnly: errorsList && !errorsList[field], // Campo non modificabile se non ci sono errori
											}}
											disabled={
												errorsList &&
												!errorsList[field] &&
												currentPlick?.[field] === formValues[field]
											}
											variant="outlined"
										/>
									</>
								) : (
									<>
										{channelType}
										<SepaPhoneNumberInput
											label={t(`form.${field.replace("_", " ")}`, {
												ns: "plick",
											})} // Etichetta leggibile
											errorsList={errorsList}
											field={field}
											setErrorsList={setErrorsList}
											handleChange={handleChange}
											disabled={
												errorsList &&
												!errorsList[field] &&
												currentPlick?.[field] === formValues[field]
											}
											phoneValue={formValues?.[field]}
										/>
									</>
								)
							}
							case "receiverIbanFromBank": {
								return (
									<AndroidSwitch
										label={t(`form.${field.replace("_", " ")}`, {
											ns: "plick",
										})} // Etichetta leggibile
										errorsList={errorsList}
										field={field}
										handleChange={handleChange}
										disabled={
											errorsList &&
											!errorsList[field] &&
											currentPlick?.[field] === formValues[field]
										}
										isCheckedProps={formValues?.[field]}
									/>
								)
							}
							default: {
								return (
									<TextField
										key={field}
										label={t(`form.${field.replace("_", " ")}`, {
											ns: "plick",
										})} // Etichetta leggibile
										value={formValues[field]}
										onChange={(event: ChangeEvent<HTMLInputElement>) =>
											handleChange(field)(event)
										}
										error={Boolean(errorsList && errorsList[field])} // Mostra errore solo se presente
										helperText={
											errorsList && errorsList[field]
												? `Error: ${errorsList[field]}`
												: ""
										} // Messaggio di errore
										InputProps={{
											readOnly:
												errorsList &&
												!errorsList[field] &&
												currentPlick?.[field] === formValues[field], // Campo non modificabile se non ci sono errori
										}}
										disabled={
											errorsList &&
											!errorsList[field] &&
											currentPlick?.[field] === formValues[field]
										}
										variant="outlined"
									/>
								)
							}
						}
					})}
				</Box>
			</Box>{" "}
			{/* Footer */}
			<Box
				sx={{
					p: 2,
					borderTop: "1px solid #ccc",
					display: "flex",
					justifyContent: "space-between",
					gap: 1,
				}}
			>
				<Button
					variant="outlined"
					color="primary"
					onClick={() => setIsDrawerOpen(false)}
				>
					{" "}
					{t("buttons.cancel", { ns: "plick" })}{" "}
				</Button>
				{isSavingForm ? (
					<CircularProgress size="50px" />
				) : (
					<Button
						variant="contained"
						color="primary"
						disabled={!isSavable}
						onClick={handleUpdate}
					>
						{" "}
						{t("buttons.update", { ns: "plick" })}{" "}
					</Button>
				)}
			</Box>
		</Box>
	)
}

export default PlickDrawerForm
